import {
  AuditOutlined,
  BookOutlined,
  CloseCircleOutlined,
  CommentOutlined,
  DoubleRightOutlined,
  FileTextOutlined,
  FormOutlined,
  HighlightOutlined,
  LinkOutlined,
  MailOutlined,
  MinusSquareFilled,
  RetweetOutlined,
  RollbackOutlined,
  SnippetsOutlined,
  UpCircleFilled,
  WhatsAppOutlined,
} from '@ant-design/icons';
import { Button, Dropdown, Tooltip } from 'antd';
import { Trans, useTranslation } from 'react-i18next';

import { CandidatePhoneContactSerializer } from '../../types/api';

interface ApplicantNavigatorActionsDropdownProps {
  location: 'TALENT_POOL' | 'CANDIDATE_ANALYSIS';
  moveStage?: boolean;
  onMoveStageClick?: () => void;
  reject?: boolean;
  onRejectClick?: () => void;
  rollback?: boolean;
  onRollbackClick?: () => void;
  givenUp?: boolean;
  onGivenUpClick?: () => void;
  select?: boolean;
  onSelectClick?: () => void;
  comment?: boolean;
  onCommentClick?: () => void;
  startInterview?: boolean;
  onStartInterviewClick?: () => void;
  sendEmail?: boolean;
  onSendEmailClick?: () => void;
  sendTests?: boolean;
  onSendTestsClick?: () => void;
  sendCustomTests?: boolean;
  onSendCustomTestsClick?: () => void;
  disableSendAdditionalQuestions?: boolean;
  sendAdditionalQuestions?: boolean;
  onSendAdditionalQuestionsClick?: () => void;
  disableSendSankhyaAdmission?: boolean;
  sendSankhyaAdmission?: boolean;
  onSendSankhyaAdmissionClick?: () => void;
  candidateWhatsApp?: CandidatePhoneContactSerializer;
  sendWhatsApp?: boolean;
  sendPracticalTechnicalCase?: boolean;
  onSendPracticalTechnicalCaseClick?: () => void;
}

const ApplicantNavigatorActionsDropdown = ({
  location,
  moveStage,
  onMoveStageClick,
  reject,
  onRejectClick,
  rollback,
  onRollbackClick,
  givenUp,
  onGivenUpClick,
  comment,
  onCommentClick,
  startInterview,
  onStartInterviewClick,
  select,
  onSelectClick,
  disableSendAdditionalQuestions,
  sendAdditionalQuestions,
  onSendAdditionalQuestionsClick,
  sendCustomTests,
  onSendCustomTestsClick,
  sendEmail,
  onSendEmailClick,
  sendTests,
  onSendTestsClick,
  disableSendSankhyaAdmission,
  sendSankhyaAdmission,
  onSendSankhyaAdmissionClick,
  sendWhatsApp,
  candidateWhatsApp,
  sendPracticalTechnicalCase,
  onSendPracticalTechnicalCaseClick,
}: ApplicantNavigatorActionsDropdownProps) => {
  const { t } = useTranslation();

  return (
    <Dropdown
      menu={{
        onClick: ({ key }) => {
          switch (key) {
            case 'stage':
              moveStage && onMoveStageClick?.();
              break;
            case 'reject':
              reject && onRejectClick?.();
              break;
            case 'rollback':
              rollback && onRollbackClick?.();
              break;
            case 'giveup':
              givenUp && onGivenUpClick?.();
              break;
            case 'select':
              select && onSelectClick?.();
              break;
            case 'annotation':
              comment && onCommentClick?.();
              break;
            case 'email':
              sendEmail && onSendEmailClick?.();
              break;
            case 'tests':
              sendTests && onSendTestsClick?.();
              break;
            case 'custom-tests':
              sendCustomTests && onSendCustomTestsClick?.();
              break;
            case 'additional_questions':
              sendAdditionalQuestions && onSendAdditionalQuestionsClick?.();
              break;
            case 'send-sankhya-admission':
              sendSankhyaAdmission && onSendSankhyaAdmissionClick?.();
              break;
            case 'start-interview':
              startInterview && onStartInterviewClick?.();
              break;
            case 'practical-technical-case':
              sendPracticalTechnicalCase && onSendPracticalTechnicalCaseClick?.();
              break;
          }
        },
        items: [
          moveStage
            ? {
                key: 'stage',
                icon: <DoubleRightOutlined />,
                label: t('Mover etapa'),
              }
            : null,
          reject ? { key: 'reject', icon: <MinusSquareFilled />, label: t('Reprovar') } : null,
          rollback
            ? {
                key: 'rollback',
                icon: <RollbackOutlined />,
                label: t('Retornar candidato'),
              }
            : null,
          moveStage || reject || rollback ? { type: 'divider' as const } : null,
          sendSankhyaAdmission
            ? {
                key: 'send-sankhya-admission',
                icon: <RetweetOutlined />,
                label: (
                  <Tooltip
                    title={disableSendSankhyaAdmission ? 'Já há uma admissão sendo enviada' : ''}
                  >
                    <Trans>Enviar admissão</Trans>
                  </Tooltip>
                ),
                disabled: disableSendSankhyaAdmission,
              }
            : null,
          givenUp
            ? {
                key: 'giveup',
                icon: <CloseCircleOutlined />,
                label: t('Marcar desistente'),
              }
            : null,
          select
            ? {
                key: 'select',
                icon: <DoubleRightOutlined />,
                label:
                  location === 'CANDIDATE_ANALYSIS' ? t('Adicionar à vaga') : t('Triar para vaga'),
              }
            : null,
          comment
            ? { key: 'annotation', icon: <FormOutlined />, label: t('Registrar comentário') }
            : null,
          startInterview
            ? {
                key: 'start-interview',
                icon: <HighlightOutlined />,
                label: t('Iniciar entrevista'),
              }
            : null,
          sendEmail ||
          sendAdditionalQuestions ||
          sendCustomTests ||
          sendTests ||
          sendPracticalTechnicalCase
            ? {
                key: 'send',
                label: t('Enviar e-mail'),
                icon: <MailOutlined />,
                children: [
                  sendEmail
                    ? { key: 'email', icon: <CommentOutlined />, label: t('Enviar comunicação') }
                    : null,
                  sendTests
                    ? { key: 'tests', icon: <AuditOutlined />, label: t('Enviar testes') }
                    : null,
                  sendCustomTests
                    ? {
                        key: 'custom-tests',
                        icon: <BookOutlined />,
                        label: t('Enviar testes customizados'),
                      }
                    : null,
                  sendAdditionalQuestions
                    ? {
                        key: 'additional_questions',
                        icon: <FileTextOutlined />,
                        disabled: disableSendAdditionalQuestions,
                        label: (
                          <Tooltip
                            title={
                              disableSendAdditionalQuestions
                                ? t('O candidato já respondeu as perguntas de pré-requisito')
                                : ''
                            }
                          >
                            <Trans>Enviar perguntas de pré-requisito</Trans>
                          </Tooltip>
                        ),
                      }
                    : null,
                  sendPracticalTechnicalCase
                    ? {
                        key: 'practical-technical-case',
                        icon: <SnippetsOutlined />,
                        label: t('Enviar case'),
                      }
                    : null,
                ],
              }
            : null,
          sendWhatsApp
            ? {
                key: 'whatsapp',
                icon: <WhatsAppOutlined />,
                disabled: !candidateWhatsApp,
                label: (
                  <a
                    href={`https://api.whatsapp.com/send?phone=${
                      candidateWhatsApp?.country_code ?? 55
                    }${candidateWhatsApp?.number ?? ''}`}
                    target="_blank"
                  >
                    <Trans>Enviar WhatsApp</Trans> <LinkOutlined />
                  </a>
                ),
              }
            : null,
        ],
      }}
    >
      <Button type="primary" icon={<UpCircleFilled />}>
        {' '}
        <Trans>Ações</Trans>
      </Button>
    </Dropdown>
  );
};

export default ApplicantNavigatorActionsDropdown;
